import React, { useState, useEffect } from 'react';
import { ordersApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from 'sonner';
import { Loader2, Pencil, Trash2 } from 'lucide-react';

/**
 * Edit a recorded invoice payment. Editable by anyone who can record payments
 * (admin, production, super_admin). Delete is super_admin only (backend gated).
 *
 * Props:
 *   open, orderId, dispatchId, payment, invoiceNumber, onClose, onSaved
 */
const EditPaymentDialog = ({ open, orderId, dispatchId, payment, invoiceNumber, onClose, onSaved }) => {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!open || !payment) return;
    setForm({
      amount: payment.amount ?? '',
      payment_mode: payment.payment_mode || 'rtgs',
      reference_number: payment.reference_number || '',
      date: (payment.date || '').slice(0, 10),
      notes: payment.notes || '',
      metal_exchange_weight: payment.metal_exchange_weight ?? '',
      metal_exchange_invoice_number: payment.metal_exchange_invoice_number || '',
    });
  }, [open, payment]);

  if (!payment) return null;
  const isMetalExchange = form.payment_mode === 'metal_exchange';
  const canDelete = user?.role === 'super_admin';

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        amount: Number(form.amount),
        payment_mode: form.payment_mode,
        reference_number: form.reference_number,
        date: form.date,
        notes: form.notes,
      };
      if (isMetalExchange) {
        payload.metal_exchange_weight = Number(form.metal_exchange_weight) || 0;
        payload.metal_exchange_invoice_number = form.metal_exchange_invoice_number || '';
      }
      const res = await ordersApi.updatePayment(orderId, dispatchId, payment.id, payload);
      const changed = Object.keys(res?.data?.changes || {}).length;
      if (changed === 0) toast.info('No changes to save');
      else toast.success(`Payment updated (${changed} field${changed === 1 ? '' : 's'})`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update payment');
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await ordersApi.deletePayment(orderId, dispatchId, payment.id);
      toast.success('Payment deleted');
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete payment');
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && !saving && !deleting && onClose?.()}>
        <DialogContent className="max-w-md" data-testid="edit-payment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-4 w-4 text-violet-600 dark:text-violet-400" />
              Edit payment
            </DialogTitle>
            <DialogDescription>
              Payment on Invoice <strong>{invoiceNumber || dispatchId?.slice(0, 8)}</strong>
              {payment.payment_type ? ` · type: ${payment.payment_type}` : ''}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Amount (₹) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.amount}
                  onChange={e => setField('amount', e.target.value)}
                  data-testid="edit-payment-amount"
                />
              </div>
              <div>
                <Label className="text-xs">Date *</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={e => setField('date', e.target.value)}
                  data-testid="edit-payment-date"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Mode</Label>
                <Select value={form.payment_mode} onValueChange={v => setField('payment_mode', v)}>
                  <SelectTrigger data-testid="edit-payment-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rtgs">RTGS / NEFT</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="metal_exchange">Metal Exchange</SelectItem>
                    <SelectItem value="sales_return">Sales Return</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Reference #</Label>
                <Input
                  value={form.reference_number}
                  onChange={e => setField('reference_number', e.target.value)}
                  placeholder="UTR / cheque #"
                  data-testid="edit-payment-reference"
                />
              </div>
            </div>

            {isMetalExchange && (
              <div className="grid grid-cols-2 gap-3 rounded-md bg-amber-50 dark:bg-amber-500/10 p-2 border border-amber-200 dark:border-amber-500/30">
                <div>
                  <Label className="text-xs">Exchange weight (g, 999)</Label>
                  <Input
                    type="number"
                    step="0.001"
                    value={form.metal_exchange_weight}
                    onChange={e => setField('metal_exchange_weight', e.target.value)}
                    data-testid="edit-payment-metal-weight"
                  />
                </div>
                <div>
                  <Label className="text-xs">Exchange invoice #</Label>
                  <Input
                    value={form.metal_exchange_invoice_number}
                    onChange={e => setField('metal_exchange_invoice_number', e.target.value)}
                    data-testid="edit-payment-metal-invoice"
                  />
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={e => setField('notes', e.target.value)}
                data-testid="edit-payment-notes"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <div>
              {canDelete && (
                <Button
                  variant="ghost"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-500/10"
                  onClick={() => setConfirmDelete(true)}
                  disabled={saving || deleting}
                  data-testid="edit-payment-delete-btn"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={saving || deleting}>Cancel</Button>
              <Button
                onClick={save}
                disabled={saving || deleting || !form.amount || !form.date}
                className="bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="edit-payment-save"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Pencil className="h-4 w-4 mr-1" />}
                Save
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="edit-payment-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this payment?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the payment of ₹{Number(payment.amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })} from Invoice
              {' '}<strong>{invoiceNumber || dispatchId?.slice(0, 8)}</strong> and increase the outstanding balance. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="edit-payment-delete-confirm-btn"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}
              Delete payment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default EditPaymentDialog;
